import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { getTeacherClasses as getTeacherClassesService } from "@/services/teacherClassService";

const assignmentDueDateSchema = z.object({
  due_date: z.string().nullable().optional(),
});

export interface ChallengeAssignment {
  id: string;
  challenge_id: string;
  student_id: string;
  assigned_by: string;
  assignment_status: "prepared" | "assigned";
  prepared_at: string;
  actually_assigned_at?: string;
  due_date?: string;
}

export interface StudentSubmission {
  id: string;
  description: string;
  files_urls: string[];
  created_at: string;
  sharing_option: string;
  version: number;
  is_latest: boolean;
  feedback_status: "pending" | "draft" | "returned" | "approved";
}

export interface TeamInfo {
  teamId: string;
  teamName: string | null;
  isLeader: boolean;
  memberCount: number;
}

export interface AssignedStudentWithSubmission {
  assignment: ChallengeAssignment;
  studentName: string;
  submission?: StudentSubmission;
  teamInfo?: TeamInfo;
  acceptedAt?: string;
  hasAccepted?: boolean;
}

export interface AssignedChallengeWithTeacher {
  challengeId: string;
  teacherId: string;
  teacherName: string;
  assignedAt: string;
  dueDate?: string | null;
}

export async function getPreparedStudents(challengeId: string): Promise<ChallengeAssignment[]> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from("challenge_assignments")
    .select("*")
    .eq("challenge_id", challengeId)
    .eq("assigned_by", user.id);

  if (error) {
    console.error("[getPreparedStudents] Error:", error);
    throw error;
  }

  return (data || []).map((item) => ({
    id: item.id,
    challenge_id: item.challenge_id,
    student_id: item.student_id,
    assigned_by: item.assigned_by,
    assignment_status: item.assignment_status as "prepared" | "assigned",
    prepared_at: item.prepared_at,
    actually_assigned_at: item.actually_assigned_at,
    due_date: item.due_date,
  }));
}

export async function prepareStudentList(challengeId: string, studentIds: string[]): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  console.log("[prepareStudentList] Preparing students:", studentIds.length);

  // Get students who are already assigned (not just prepared)
  const { data: existingAssigned } = await supabase
    .from("challenge_assignments")
    .select("student_id")
    .eq("challenge_id", challengeId)
    .eq("assignment_status", "assigned");

  const alreadyAssignedIds = new Set((existingAssigned || []).map((a) => a.student_id));

  // Delete existing prepared assignments
  const { error: deleteError } = await supabase
    .from("challenge_assignments")
    .delete()
    .eq("challenge_id", challengeId)
    .eq("assigned_by", user.id)
    .eq("assignment_status", "prepared");

  if (deleteError) {
    console.error("[prepareStudentList] Error deleting:", deleteError);
    throw deleteError;
  }

  // Filter out students who are already assigned
  const studentsToPrepar = studentIds.filter((id) => !alreadyAssignedIds.has(id));

  // Insert new prepared assignments (with due_date from challenge default if set)
  if (studentsToPrepar.length > 0) {
    const { data: challenge } = await supabase
      .from("challenges")
      .select("default_due_date")
      .eq("id", challengeId)
      .single();

    const defaultDueDate = challenge?.default_due_date ?? null;
    const dueDateIso = defaultDueDate != null ? `${defaultDueDate}T23:59:59.000Z` : null;

    const assignments = studentsToPrepar.map((studentId) => ({
      challenge_id: challengeId,
      student_id: studentId,
      assigned_by: user.id,
      assignment_status: "prepared" as const,
      prepared_at: new Date().toISOString(),
      ...(dueDateIso != null && { due_date: dueDateIso }),
    }));

    const { error: insertError } = await supabase.from("challenge_assignments").insert(assignments);

    if (insertError) {
      console.error("[prepareStudentList] Error inserting:", insertError);
      throw insertError;
    }

    console.log("[prepareStudentList] Successfully prepared", assignments.length, "students");
  }
}

export async function assignChallengeToStudents(challengeId: string): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  const { error } = await supabase
    .from("challenge_assignments")
    .update({
      assignment_status: "assigned",
      actually_assigned_at: new Date().toISOString(),
    })
    .eq("challenge_id", challengeId)
    .eq("assigned_by", user.id)
    .eq("assignment_status", "prepared");

  if (error) {
    console.error("[assignChallengeToStudents] Error:", error);
    throw error;
  }
}

export async function removeAssignment(challengeId: string, studentId: string): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  // First, remove from any team for this challenge
  const { data: teamMemberships } = await supabase
    .from("challenge_team_members")
    .select("id, team_id")
    .eq("student_id", studentId);

  if (teamMemberships && teamMemberships.length > 0) {
    // Check which membership is for this challenge
    for (const membership of teamMemberships) {
      const { data: team } = await supabase
        .from("challenge_teams")
        .select("id, challenge_id, created_by")
        .eq("id", membership.team_id)
        .eq("challenge_id", challengeId)
        .single();

      if (team) {
        const teamId = team.id;
        const wasLeader = team.created_by === studentId;

        // Remove from this team
        await supabase.from("challenge_team_members").delete().eq("id", membership.id);

        // Check remaining members
        const { data: remainingMembers } = await supabase
          .from("challenge_team_members")
          .select("student_id, joined_at")
          .eq("team_id", teamId)
          .order("joined_at", { ascending: true });

        if (!remainingMembers || remainingMembers.length === 0) {
          // No members left - delete the team
          await supabase.from("challenge_teams").delete().eq("id", teamId);
        } else if (wasLeader) {
          // Leader was removed - assign new leader (first by join date)
          const newLeaderId = remainingMembers[0].student_id;
          await supabase
            .from("challenge_teams")
            .update({ created_by: newLeaderId })
            .eq("id", teamId);
        }

        break;
      }
    }
  }

  // Then remove the assignment
  const { error } = await supabase
    .from("challenge_assignments")
    .delete()
    .eq("challenge_id", challengeId)
    .eq("student_id", studentId)
    .eq("assigned_by", user.id);

  if (error) {
    console.error("[removeAssignment] Error:", error);
    throw error;
  }
}

export async function getAssignmentStatus(challengeId: string): Promise<{
  preparedCount: number;
  assignedCount: number;
  status: "none" | "prepared" | "assigned" | "mixed";
}> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return { preparedCount: 0, assignedCount: 0, status: "none" };

  const { data, error } = await supabase
    .from("challenge_assignments")
    .select("assignment_status")
    .eq("challenge_id", challengeId)
    .eq("assigned_by", user.id);

  if (error) {
    console.error("[getAssignmentStatus] Error:", error);
    throw error;
  }

  if (!data || data.length === 0) {
    return { preparedCount: 0, assignedCount: 0, status: "none" };
  }

  const preparedCount = data.filter((a) => a.assignment_status === "prepared").length;
  const assignedCount = data.filter((a) => a.assignment_status === "assigned").length;

  let status: "none" | "prepared" | "assigned" | "mixed" = "none";
  if (preparedCount > 0 && assignedCount === 0) status = "prepared";
  else if (assignedCount > 0 && preparedCount === 0) status = "assigned";
  else if (preparedCount > 0 && assignedCount > 0) status = "mixed";

  return { preparedCount, assignedCount, status };
}

/**
 * Cheap query that returns only the most recent `actually_assigned_at` timestamp
 * for the currently logged-in student. Used by useNewAssignmentNotifier to detect
 * newly assigned challenges without refetching the whole assignment list.
 *
 * Returns null if the student has no assignments or is not logged in.
 */
export async function getLatestAssignedAtForCurrentStudent(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from("challenge_assignments")
    .select("actually_assigned_at")
    .eq("student_id", user.id)
    .eq("assignment_status", "assigned")
    .not("actually_assigned_at", "is", null)
    .order("actually_assigned_at", { ascending: false, nullsFirst: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error("[getLatestAssignedAtForCurrentStudent] Error:", error);
    return null;
  }

  return data?.actually_assigned_at ?? null;
}

export async function getStudentAssignedChallenges(): Promise<AssignedChallengeWithTeacher[]> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return [];

  console.log("[getStudentAssignedChallenges] Fetching assigned challenges for student:", user.id);

  // Get challenges assigned to this student
  const { data: assignments, error: assignmentsError } = await supabase
    .from("challenge_assignments")
    .select("challenge_id, assigned_by, actually_assigned_at, created_at, due_date")
    .eq("student_id", user.id)
    .eq("assignment_status", "assigned")
    .order("actually_assigned_at", { ascending: false, nullsFirst: false });

  if (assignmentsError) {
    console.error("[getStudentAssignedChallenges] Error fetching assignments:", assignmentsError);
    throw assignmentsError;
  }

  if (!assignments || assignments.length === 0) {
    console.log("[getStudentAssignedChallenges] No assigned challenges found");
    return [];
  }

  console.log("[getStudentAssignedChallenges] Found assignments:", assignments);

  // Get teacher profiles
  const teacherIds = [...new Set(assignments.map((a) => a.assigned_by))];
  const { data: teachers, error: teachersError } = await supabase
    .from("profiles")
    .select("id, first_name, last_name, display_name")
    .in("id", teacherIds);

  if (teachersError) {
    console.error("[getStudentAssignedChallenges] Error fetching teachers:", teachersError);
    throw teachersError;
  }

  console.log("[getStudentAssignedChallenges] Found teachers:", teachers);

  // Combine data
  const result = assignments.map((assignment) => {
    const teacher = teachers?.find((t) => t.id === assignment.assigned_by);
    const teacherName = teacher
      ? teacher.display_name ||
        (teacher.first_name && teacher.last_name
          ? `${teacher.first_name} ${teacher.last_name}`
          : "Neznámý učitel")
      : "Neznámý učitel";

    const parsedAssignment = assignmentDueDateSchema.safeParse(assignment);
    const dueDate = parsedAssignment.success ? (parsedAssignment.data.due_date ?? null) : null;

    return {
      challengeId: assignment.challenge_id,
      teacherId: assignment.assigned_by,
      teacherName,
      assignedAt: assignment.actually_assigned_at || assignment.created_at || "",
      dueDate,
    };
  });

  console.log("[getStudentAssignedChallenges] Returning result:", result);
  return result;
}

export async function getAssignedStudentsWithSubmissions(
  challengeId: string
): Promise<AssignedStudentWithSubmission[]> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return [];

  // Get assigned students
  const { data: assignments, error: assignmentsError } = await supabase
    .from("challenge_assignments")
    .select("*")
    .eq("challenge_id", challengeId)
    .eq("assigned_by", user.id)
    .eq("assignment_status", "assigned");

  if (assignmentsError) {
    console.error(
      "[getAssignedStudentsWithSubmissions] Error fetching assignments:",
      assignmentsError
    );
    throw assignmentsError;
  }

  if (!assignments || assignments.length === 0) return [];

  // Get student profiles
  const studentIds = assignments.map((a) => a.student_id);
  const { data: profiles, error: profilesError } = await supabase
    .from("profiles")
    .select("id, display_name, first_name, last_name")
    .in("id", studentIds);

  if (profilesError) {
    console.error("[getAssignedStudentsWithSubmissions] Error fetching profiles:", profilesError);
    throw profilesError;
  }

  // Get team memberships for these students in this challenge (needed before we know teamIds)
  const { data: teamMemberships, error: teamError } = await supabase
    .from("challenge_team_members")
    .select(
      `
      student_id,
      team_id,
      team:challenge_teams!challenge_team_members_team_id_fkey(
        id,
        challenge_id,
        created_by,
        team_name
      )
    `
    )
    .in("student_id", studentIds);

  if (teamError) {
    console.error(
      "[getAssignedStudentsWithSubmissions] Error fetching team memberships:",
      teamError
    );
    // Don't throw - teams are optional
  }

  // Build map of student -> team info
  const studentTeamMap = new Map<
    string,
    { teamId: string; teamName: string | null; leaderId: string }
  >();
  const teamMemberCounts = new Map<string, number>();

  if (teamMemberships) {
    // Count members per team
    for (const m of teamMemberships) {
      const team = m.team as any;
      if (team && team.challenge_id === challengeId) {
        teamMemberCounts.set(team.id, (teamMemberCounts.get(team.id) || 0) + 1);
      }
    }

    // Map student -> team
    for (const m of teamMemberships) {
      const team = m.team as any;
      if (team && team.challenge_id === challengeId) {
        studentTeamMap.set(m.student_id, {
          teamId: team.id,
          teamName: team.team_name,
          leaderId: team.created_by,
        });
      }
    }
  }

  const teamIds = Array.from(new Set(Array.from(studentTeamMap.values()).map((t) => t.teamId)));

  // Latest teacher-shared submission per (user_id, team_id), not overall latest
  const key = (r: { user_id: string; team_id?: string | null }) =>
    `${r.user_id}:${r.team_id ?? "null"}`;
  const latestByKey = new Map<
    string,
    {
      user_id: string;
      team_id?: string | null;
      id: string;
      description: string | null;
      files_urls: string[] | null;
      created_at: string;
      sharing_option: string;
      version: number;
      is_latest: boolean;
      feedback_status: string | null;
    }
  >();

  const { data: individualRows } = await supabase
    .from("challenge_results")
    .select(
      "user_id, team_id, id, description, files_urls, created_at, sharing_option, version, is_latest, feedback_status"
    )
    .eq("challenge_id", challengeId)
    .in("user_id", studentIds)
    .eq("shared_with_teacher_id", user.id)
    .is("team_id", null)
    .order("version", { ascending: false });
  for (const r of individualRows || []) {
    const k = key(r);
    if (!latestByKey.has(k)) latestByKey.set(k, r as any);
  }

  if (teamIds.length > 0) {
    const { data: teamRows } = await supabase
      .from("challenge_results")
      .select(
        "user_id, team_id, id, description, files_urls, created_at, sharing_option, version, is_latest, feedback_status"
      )
      .eq("challenge_id", challengeId)
      .in("team_id", teamIds)
      .eq("shared_with_teacher_id", user.id)
      .order("version", { ascending: false });
    for (const r of teamRows || []) {
      const k = key(r);
      if (!latestByKey.has(k)) latestByKey.set(k, r as any);
    }
  }

  const allLatest = [...latestByKey.values()];
  const submissions = allLatest.filter((s) => !s.team_id);
  const teamSubmissions = allLatest.filter((s) => s.team_id);

  // Fetch acceptance status for all assigned students
  const { data: progressRows } = await supabase
    .from("user_challenge_progress")
    .select("user_id, created_at, status")
    .eq("challenge_id", challengeId)
    .in("user_id", studentIds)
    .neq("status", "not_started");

  const acceptanceMap = new Map<string, string>();
  for (const row of progressRows || []) {
    acceptanceMap.set(row.user_id, row.created_at);
  }

  // Combine data
  return assignments.map((assignment) => {
    const profile = profiles?.find((p) => p.id === assignment.student_id);

    let submission = submissions?.find((s) => s.user_id === assignment.student_id);

    // Get team info
    const teamData = studentTeamMap.get(assignment.student_id);
    let teamInfo: TeamInfo | undefined;

    if (teamData) {
      // If no individual submission, check for team submission
      if (!submission) {
        submission = teamSubmissions.find((s) => s.team_id === teamData.teamId);
      }

      teamInfo = {
        teamId: teamData.teamId,
        teamName: teamData.teamName,
        isLeader: teamData.leaderId === assignment.student_id,
        memberCount: teamMemberCounts.get(teamData.teamId) || 1,
      };
    }

    const studentName = (() => {
      if (profile?.first_name && profile?.last_name) {
        return `${profile.first_name} ${profile.last_name}`;
      }
      if (profile?.first_name) return profile.first_name;
      if (profile?.last_name) return profile.last_name;
      return profile?.display_name || "Neznámý žák";
    })();

    const acceptedAt = acceptanceMap.get(assignment.student_id);

    return {
      assignment: {
        id: assignment.id,
        challenge_id: assignment.challenge_id,
        student_id: assignment.student_id,
        assigned_by: assignment.assigned_by,
        assignment_status: assignment.assignment_status as "prepared" | "assigned",
        prepared_at: assignment.prepared_at,
        actually_assigned_at: assignment.actually_assigned_at,
        due_date: assignment.due_date,
      },
      studentName,
      submission: submission
        ? {
            id: submission.id,
            description: submission.description,
            files_urls: submission.files_urls || [],
            created_at: submission.created_at,
            sharing_option: submission.sharing_option,
            version: submission.version,
            is_latest: submission.is_latest,
            feedback_status: (submission.feedback_status || "pending") as
              | "pending"
              | "draft"
              | "returned"
              | "approved",
          }
        : undefined,
      teamInfo,
      acceptedAt,
      hasAccepted: !!acceptedAt,
    };
  });
}

export interface StudentSolutionInfo {
  studentId: string;
  studentName: string;
  submission: StudentSubmission;
}

/**
 * Get student solutions for a challenge: class students who shared work with this teacher.
 * Scoped to shared_with_teacher_id = current user (not other teachers' reviews).
 */
export async function getStudentSolutionsForChallenge(
  challengeId: string
): Promise<StudentSolutionInfo[]> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    console.log("[getStudentSolutionsForChallenge] No user found");
    return [];
  }

  console.log("[getStudentSolutionsForChallenge] Fetching solutions for challenge:", challengeId);

  // Step 1: Get teacher's classes
  const classIds = await getTeacherClassesService(user.id);
  if (classIds.length === 0) {
    return [];
  }

  // Step 2: Get students from those classes
  const { data: classStudents, error: studentsError } = await supabase
    .from("class_students")
    .select("student_id")
    .in("class_id", classIds);

  if (studentsError) {
    console.error("[getStudentSolutionsForChallenge] Error fetching students:", studentsError);
    throw studentsError;
  }

  if (!classStudents || classStudents.length === 0) {
    console.log("[getStudentSolutionsForChallenge] No students found");
    return [];
  }

  const studentIds = [...new Set(classStudents.map((cs) => cs.student_id))];
  console.log("[getStudentSolutionsForChallenge] Found students:", studentIds.length);

  // Step 3: Latest teacher-shared submission per (user_id, team_id), only for this teacher
  const { data: sharedRows, error: submissionsError } = await supabase
    .from("challenge_results")
    .select(
      "user_id, team_id, id, description, files_urls, created_at, sharing_option, version, is_latest, feedback_status"
    )
    .eq("challenge_id", challengeId)
    .in("user_id", studentIds)
    .eq("shared_with_teacher_id", user.id)
    .order("version", { ascending: false });

  if (submissionsError) {
    console.error(
      "[getStudentSolutionsForChallenge] Error fetching submissions:",
      submissionsError
    );
    throw submissionsError;
  }

  const solutionKey = (r: { user_id: string; team_id?: string | null }) =>
    `${r.user_id}:${r.team_id ?? "null"}`;
  const latestByKey = new Map<string, (typeof sharedRows)[0]>();
  for (const r of sharedRows || []) {
    const k = solutionKey(r);
    if (!latestByKey.has(k)) latestByKey.set(k, r);
  }
  const submissions = [...latestByKey.values()].sort((a, b) =>
    (b.created_at || "").localeCompare(a.created_at || "")
  );

  if (submissions.length === 0) {
    console.log("[getStudentSolutionsForChallenge] No submissions found");
    return [];
  }

  console.log("[getStudentSolutionsForChallenge] Found submissions:", submissions.length);

  // Step 4: Get profiles for students who submitted
  const submittedStudentIds = submissions.map((s) => s.user_id);
  const { data: profiles, error: profilesError } = await supabase
    .from("profiles")
    .select("id, display_name, first_name, last_name")
    .in("id", submittedStudentIds);

  if (profilesError) {
    console.error("[getStudentSolutionsForChallenge] Error fetching profiles:", profilesError);
    throw profilesError;
  }

  // Step 5: Combine data
  const result: StudentSolutionInfo[] = submissions.map((submission) => {
    const profile = profiles?.find((p) => p.id === submission.user_id);

    const studentName = (() => {
      if (profile?.first_name && profile?.last_name) {
        return `${profile.first_name} ${profile.last_name}`;
      }
      if (profile?.first_name) return profile.first_name;
      if (profile?.last_name) return profile.last_name;
      return profile?.display_name || "Neznámý žák";
    })();

    return {
      studentId: submission.user_id,
      studentName,
      submission: {
        id: submission.id,
        description: submission.description,
        files_urls: submission.files_urls || [],
        created_at: submission.created_at,
        sharing_option: submission.sharing_option,
        version: submission.version,
        is_latest: submission.is_latest,
        feedback_status: (submission.feedback_status || "pending") as
          | "pending"
          | "draft"
          | "returned"
          | "approved",
      },
    };
  });

  console.log("[getStudentSolutionsForChallenge] Returning solutions:", result.length);
  return result;
}
